/**
 * Iter 67cl — Popular Searches chip strip.
 *
 * Fetches `/public/popular-searches?limit=6` and renders the top renter-search
 * destinations from the last 30 days as quick-launch chips. Clicking a chip
 * navigates to `/cars?country=...` or `/cars?pickup_location=...` and fires a
 * `POST /public/track-search` so we count taps too, not just initial loads.
 *
 * Self-hides when fewer than 3 searches have happened in 30 days — a one or
 * two-chip strip looks broken, better to keep it off until there's real
 * signal to surface.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { TrendingUp, MapPin, ArrowRight } from "lucide-react";

const FALLBACK = [
  { label: "Italy",    country: "Italy",    location: null, count: null },
  { label: "Spain",    country: "Spain",    location: null, count: null },
  { label: "Germany",  country: "Germany",  location: null, count: null },
  { label: "France",   country: "France",   location: null, count: null },
  { label: "Albania",  country: "Albania",  location: null, count: null },
  { label: "Croatia",  country: "Croatia",  location: null, count: null },
];

function _href(item) {
  const sp = new URLSearchParams();
  if (item.location) sp.set("pickup_location", item.location);
  else if (item.country) sp.set("country", item.country);
  return `/cars?${sp.toString()}`;
}

export default function PopularSearchesSection() {
  const [items, setItems] = useState(null); // null = loading, [] = empty
  useEffect(() => {
    let alive = true;
    api.get("/public/popular-searches", { params: { limit: 6, days: 30 } })
      .then((r) => { if (alive) setItems(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, []);

  const isLive = !!(items && items.length >= 3);
  const list = useMemo(() => (isLive ? items.slice(0, 6) : FALLBACK), [isLive, items]);
  const headline = isLive ? "What renters are searching this month" : "Where renters love to drive";
  const eyebrow = isLive ? "Trending searches" : "Top destinations";

  const onChipClick = (item) => {
    // Fire-and-forget tap tracking. We don't await — the navigation should
    // be instant regardless of the analytics call.
    if (item.country || item.location) {
      api.post("/public/track-search", { country: item.country || null, location: item.location || null }).catch(() => {});
    }
  };

  return (
    <section className="bg-white py-14 border-y border-border" data-testid="popular-searches-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-2 flex items-center gap-1.5">
              <TrendingUp className="h-3 w-3" /> {eyebrow}
            </div>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight font-medium">{headline}</h2>
          </div>
          <Link to="/cars" className="text-sm font-medium text-blue-600 hover:underline" data-testid="popular-searches-see-all">
            Browse all cars <ArrowRight className="inline h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="flex flex-wrap gap-3" data-testid="popular-searches-chips">
          {list.map((item, i) => (
            <Link
              key={`${item.label}-${i}`}
              to={_href(item)}
              onClick={() => onChipClick(item)}
              className="group inline-flex items-center gap-2 pl-3 pr-4 py-2 rounded-full bg-neutral-50 hover:bg-neutral-900 hover:text-white border border-border hover:border-neutral-900 transition-all"
              data-testid={`popular-search-chip-${item.label}`}
            >
              <MapPin className="h-3.5 w-3.5 text-blue-600 group-hover:text-amber-300 transition-colors" />
              <span className="text-sm font-medium">{item.label}</span>
              {isLive && item.count > 0 && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 group-hover:text-white/70 transition-colors tabular-nums">
                  {item.count}
                </span>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
